/* eslint-disable react-hooks/set-state-in-effect -- subdomain pinning + auto-recovery
   are inherently URL-driven. The effects observe an external signal (URL slug,
   workspace-list sync state) and reconcile React state to match. The v0.1.47
   sticky-last-workspace setter follows the same pattern: it reconciles a
   derived "what workspace was the user last in" value when selectedWorkspace
   changes. */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@rocicorp/zero/react";
import { queries } from "@/lib/queries";
import {
  initializeScope,
  deriveSelectedWorkspace,
  persistScope,
} from "@/lib/scope-utils";
import {
  generateSlugFromName,
  normalizeWorkspaceSlug,
} from "@/lib/workspace-validation";
import { workspaceSlugFromUrl } from "@/lib/app-route";
import { pickTerminalWorkspaceId } from "./pick-terminal-workspace-id";

interface UserLike {
  id: string;
}

interface UseWorkspaceScopeArgs {
  user: UserLike | null | undefined;
  inviteToken: string | null;
  setSelectedChannel: (id: string | null) => void;
  closeAllSideViews: () => void;
}

/**
 * Owns the workspace-scope cursor + workspace list query + slug parsing
 * + subdomain pinning + auto-recovery for an out-of-sync workspace.
 *
 * The exported `pendingWorkspaceRef` is shared with the invite-flow hook
 * via the `setPendingWorkspace` setter so newly-created workspaces don't
 * get clobbered by auto-recovery.
 */
export function useWorkspaceScope({
  user,
  inviteToken,
  setSelectedChannel,
  closeAllSideViews,
}: UseWorkspaceScopeArgs) {
  const [scope, setScope] = useState<string>(initializeScope);
  const selectedWorkspace = deriveSelectedWorkspace(scope);

  // Workspace list — Zero is the SOLE source of truth post-Option D.
  //
  // Pre-Option D, this memo unioned Zero's region-local list with `GET
  // /cp/workspaces` so cross-region workspaces (e.g. EU-pinned for a US
  // user) showed up. After the 2026-05-18 single-region cutover every
  // workspace lives in US PG and every user's Zero binds to US PG, so
  // `queries.workspaces.byUser` returns the complete list reactively.
  //
  // The union was retained for a window after cutover and produced the
  // kicked-user UX bug: Zero correctly dropped the workspace when
  // `workspace_members.removed_at` flipped (the query filters
  // `removed_at IS NULL`), but the CP REST snapshot kept it visible
  // until the next refetch — symptom: kicked user saw the workspace
  // tile + partial channels for ages until reload. See
  // `.claude/docs/multi-region-runbook.md` § "Desktop client region
  // machinery" for the reintroduction checklist if EU PG authority
  // is ever brought back.
  const [workspaceListRaw, workspaceListStatus] = useQuery(
    user ? queries.workspaces.byUser() : null,
  );
  const workspaceList = useMemo(
    () => workspaceListRaw ?? [],
    [workspaceListRaw],
  );

  // Tracks an explicitly-selected workspace that hasn't synced to the list yet
  // (e.g. just created). Prevents auto-recovery from overriding it.
  const pendingWorkspaceRef = useRef<string | null>(null);
  const [pendingWorkspaceId, setPendingWorkspaceId] = useState<string | null>(
    null,
  );
  const setPendingWorkspace = useCallback((id: string | null) => {
    pendingWorkspaceRef.current = id;
    setPendingWorkspaceId(id);
  }, []);

  // On *.ano.dev, previously redirected to subdomain (full page reload).
  // Now always returns false so scope switching is client-side only.
  // Subdomain pinning on initial load is handled by the useEffect below.
  const navigateToSubdomain = useCallback((_slug: string) => false, []);

  // Persist selections across reloads
  useEffect(() => persistScope(scope), [scope]);

  // Workspace pinning from the URL — path (`/<slug>/…`, canonical) first,
  // falling back to the legacy subdomain host during the migration.
  const slugFromUrl = useMemo(() => workspaceSlugFromUrl(window.location), []);
  const workspaceForSubdomain = useMemo(() => {
    if (!slugFromUrl || slugFromUrl === "home") return null;
    const normalized = normalizeWorkspaceSlug(slugFromUrl);
    return workspaceList.find(
      (w) =>
        w.slug === normalized ||
        generateSlugFromName(w.name ?? "") === normalized,
    );
  }, [slugFromUrl, workspaceList]);

  // Pin to the URL slug ONCE, on initial load — then stay out of the way.
  //
  // `slugFromUrl` is captured at mount (`[]` deps) and the URL is updated via
  // `replaceState`, which never re-triggers it — so the slug is effectively
  // frozen for the lifetime of the mount. Without the latch below this effect
  // re-ran on every `scope` change and, after a user-initiated switch, found
  // `scope !== workspaceForSubdomain.id` and called `setScope(...)` straight
  // back to the mount-time workspace. Once path-based routing (#1916) replaced
  // the old full-reload subdomain redirect with a client-side `setScope`, that
  // revert turned EVERY in-app workspace switch (tabs + dropdown) into a silent
  // no-op. The ref latches after the first real pin so subsequent switches —
  // and the URL the app writes for them — are respected.
  const hasAppliedUrlPinRef = useRef(false);
  useEffect(() => {
    if (inviteToken) return; // Don't override invite's force-home scope
    if (hasAppliedUrlPinRef.current) return; // initial-load pin only
    if (slugFromUrl === "home") {
      hasAppliedUrlPinRef.current = true;
      if (scope !== "home") {
        setScope("home");
        closeAllSideViews();
      }
    } else if (workspaceForSubdomain) {
      // Latch only once the workspace list has resolved the slug to a real
      // workspace — until then keep waiting (a slug we can't resolve yet must
      // not burn the one-shot pin).
      hasAppliedUrlPinRef.current = true;
      if (scope !== workspaceForSubdomain.id) {
        setScope(workspaceForSubdomain.id);
        closeAllSideViews();
      }
    }
  }, [
    slugFromUrl,
    workspaceForSubdomain,
    inviteToken,
    scope,
    closeAllSideViews,
  ]);

  const handleSelectWorkspace = useCallback(
    (id: string | null) => {
      if (id === selectedWorkspace) return;
      // v0.1.49: mark the just-selected workspace as pending so the
      // auto-recovery effect below doesn't clobber it back to
      // workspaceList[0] before the workspace list catches up.
      // Pre-v0.1.49, `handleScopeChange` set this ref but
      // `handleSelectWorkspace` (the Sidebar's primary code path) did
      // not — so cross-region selections (where Zero is bound to the
      // OLD region and the new workspace hasn't synced yet) were
      // silently overridden to a region-local workspace. Symptom: user
      // clicked "Ano Test US 3" from an EU-bound Zero session →
      // selection got clobbered to "Test 2" (the first EU workspace in
      // the list) → credentials.json was rewritten for Test 2 instead
      // of Ano Test US 3.
      if (id) {
        pendingWorkspaceRef.current = id;
        setPendingWorkspaceId(id);
      } else {
        pendingWorkspaceRef.current = null;
        setPendingWorkspaceId(null);
      }
      setScope(id ?? "home");
      setSelectedChannel(null);
      closeAllSideViews();
    },
    [selectedWorkspace, setSelectedChannel, closeAllSideViews],
  );

  const handleScopeChange = useCallback(
    (newScope: string) => {
      if (newScope !== scope) {
        if (newScope !== "home") {
          const ws = workspaceList.find((w) => w.id === newScope);
          if (ws?.slug && navigateToSubdomain(ws.slug)) return;
          pendingWorkspaceRef.current = newScope;
          setPendingWorkspaceId(newScope);
        } else {
          pendingWorkspaceRef.current = null;
          setPendingWorkspaceId(null);
        }
        setScope(newScope);
        setSelectedChannel(null);
        closeAllSideViews();
      }
    },
    [
      scope,
      workspaceList,
      navigateToSubdomain,
      setSelectedChannel,
      closeAllSideViews,
    ],
  );

  // Auto-recover: workspace mode but selected workspace not in synced list.
  //
  // Gate on Zero's `complete` status — post-Option D every workspace
  // lives in US PG and Zero is authoritative for the user's list, so
  // there's no longer a cross-region case where Zero would lag the CP
  // snapshot.
  useEffect(() => {
    if (workspaceListStatus.type !== "complete") return;
    if (scope === "home") return;

    if (workspaceList.length > 0 && selectedWorkspace) {
      if (workspaceList.some((w) => w.id === selectedWorkspace)) {
        pendingWorkspaceRef.current = null;
        setPendingWorkspaceId(null);
        return;
      }
      // Still waiting for a just-created workspace to sync
      if (pendingWorkspaceRef.current === selectedWorkspace) return;
      // Selected workspace not in list — switch to first available
      const ws = workspaceList[0];
      if (ws.slug && navigateToSubdomain(ws.slug)) return;
      setScope(ws.id);
      closeAllSideViews();
    } else if (workspaceList.length > 0 && !selectedWorkspace) {
      // No workspace selected but we have workspaces — pick first
      const ws = workspaceList[0];
      if (ws.slug && navigateToSubdomain(ws.slug)) return;
      setScope(ws.id);
      closeAllSideViews();
    } else if (workspaceList.length === 0) {
      // No workspaces at all — go Home.
      // EXCEPT: if we're holding a just-created workspace via
      // pendingWorkspaceRef, hold the scope. CreateWorkspaceDialog
      // races this effect; without the guard we'd bounce to home for
      // ~100ms before the new workspace lands in Zero's list, leaving
      // the user on home with the workspace visible but not selected.
      if (
        pendingWorkspaceRef.current &&
        pendingWorkspaceRef.current === selectedWorkspace
      ) {
        return;
      }
      setScope("home");
      setSelectedChannel(null);
      closeAllSideViews();
    }
  }, [
    scope,
    selectedWorkspace,
    workspaceList,
    workspaceListStatus,
    navigateToSubdomain,
    setSelectedChannel,
    closeAllSideViews,
  ]);

  // Per-workspace region prefetch + `setActiveWorkspaceForZero` cursor
  // sync were deleted in the Option D collapse (2026-05-19). Single-
  // region cutover means every workspace lives in US PG; the
  // `cacheURL` for ZeroProvider is fully determined by the user's
  // `home_region`, not by which workspace they're viewing. AuthGate
  // resolves home_region ONCE at sign-in via
  // `resolveHomeRegion()` + `setHomeRegionForZero()`. Workspace
  // switches no longer touch the active-region store.
  //
  // See `.claude/docs/multi-region-runbook.md` § "Desktop client
  // region machinery" for the reintroduction checklist if EU PG
  // authority is ever brought back.

  // Look up the user's home_region from their own users row. Zero is
  // regional, but the user's row exists in every region they've been
  // referenced from (real row in home_region, cross-region stub in
  // peer regions — the stub carries the real home_region per the
  // bootstrap route). Either way, this returns the right value
  // regardless of which region Zero is currently bound to.
  const [userRow] = useQuery(user ? queries.users.byId(user.id) : null);
  const userHomeRegion: "us" | "eu" | null =
    userRow?.home_region === "us" || userRow?.home_region === "eu"
      ? userRow.home_region
      : null;

  // v0.1.47: track the LAST non-null selected workspace so the terminal
  // can stay sticky across Home transitions. Updated only on real
  // workspace selections — Home (selectedWorkspace = null) does NOT
  // reset it. State (not ref) because `pickTerminalWorkspaceId` is a
  // pure render-time computation and refs aren't allowed in render per
  // the `react-hooks/refs` rule. The extra render this triggers is
  // exactly when terminalWorkspaceId would change anyway.
  const [lastSelectedWorkspace, setLastSelectedWorkspace] = useState<
    string | null
  >(null);
  useEffect(() => {
    if (selectedWorkspace) setLastSelectedWorkspace(selectedWorkspace);
  }, [selectedWorkspace]);

  // Terminal needs a workspace for agent API calls — fall back when on
  // Home so the shell is always available. Rule lives in the pure
  // helper for test coverage; see `pick-terminal-workspace-id.ts`.
  const terminalWorkspaceId = pickTerminalWorkspaceId({
    selectedWorkspace,
    lastSelectedWorkspace,
    workspaceList,
    userHomeRegion,
  });

  return {
    scope,
    setScope,
    selectedWorkspace,
    workspaceList,
    workspaceListStatus,
    handleSelectWorkspace,
    handleScopeChange,
    navigateToSubdomain,
    setPendingWorkspace,
    pendingWorkspaceId,
    terminalWorkspaceId,
  };
}
